import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { BasicXpacInfo, DeviceLogMessage } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { IPermission } from 'src/app/shared/guards/authorized.guard';
import { getDeviceLoggingAction } from 'src/app/store/actions/devices.actions';
import { selectDeviceLogging } from 'src/app/store/selectors/devices.selectors';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { append } from 'src/app/utils/functions/string.functions';
import { IDeviceLogState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { LogDetailsDialogData, XpacLoggingDetailsComponent } from './xpac-logging-details/xpac-logging-details.component';

@UntilDestroy()
@Component({
  selector: 'xpac-xpac-logging',
  templateUrl: './xpac-logging.component.html',
  styleUrls: ['./xpac-logging.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpacLoggingComponent implements OnInit {
  private readonly defaultMaxLogs: number = 10;
  private readonly loadMoreMaxLogs: number = 50;


  @Input() public xpac: BasicXpacInfo = null;

  public deviceLogging: DeviceLogMessage[] = [];
  public isLoading: boolean = true;
  public hasMore: boolean = false;
  public isLoadingMore: boolean = false;

  private deviceLoggingState: IDeviceLogState = null;

  constructor(private store: Store<IRemoteXPACPortalState>,
    private cdr: ChangeDetectorRef,
    private datePipe: LocalizedDatePipe,
    public dialog: DialogAnimationService) { }

  public ngOnInit(): void {
    this.initListeners();
  }

  public agGetRequiredPermissions(): IPermission[] {
    return [
      { type: 'RemoteMaintenance.License', operation: OperationEnum.READ }
    ];
  }

  public onLogClick(log: DeviceLogMessage): void{
    const logDetailsDialogData: LogDetailsDialogData = {log: log};
    this.dialog.open(XpacLoggingDetailsComponent, {
      panelClass: 'my-full-screen-dialog',     
      animation: { to: "left" },
      data: logDetailsDialogData,
      disableClose: true
    }); 
  }

  public getInfoString(log: DeviceLogMessage): string{
    let infoString: string = "";
    if(log.impUser !== null){
      if(log.impUser.name !== null){
        infoString = append(infoString, log.impUser.name, ', ');
      }
      else if(log.impUser.email !== null){
        infoString = append(infoString, log.impUser.email, ', ');
      }
    }
    if(log.user !== null){
      if(log.user.name !== null){
        infoString = append(infoString, log.user.name, ', ');
      }
      else if(log.user.email !== null){
        infoString = append(infoString, log.user.email, ', ');
      }
    }

    infoString = append(infoString, this.datePipe.transform(log.timeStampUtc, 'short'), ' - ');
    return infoString;
  }

  public loadMore(): void {
    if (
      this.isLoadingMore ||
      !this.deviceLoggingState ||
      !this.deviceLoggingState.continuationToken
    ) {
      return;
    }

    this.isLoadingMore = true;

    this.store.dispatch(
      getDeviceLoggingAction({
        data: {
          deviceId: this.xpac.deviceId,
          maxLogs: this.loadMoreMaxLogs,
          continuationToken: this.deviceLoggingState.continuationToken,
          append: true
        }
      })
    );
  }

  private initListeners(): void {
    this.store.dispatch(getDeviceLoggingAction({ data: { deviceId: this.xpac.deviceId, maxLogs: this.defaultMaxLogs, append: false }}));

    this.store.select(selectDeviceLogging(this.xpac.deviceId))
    .pipe(
      filter(hasValue),
      untilDestroyed(this)
    )
    .subscribe((deviceLogging: IDeviceLogState) => {
      this.deviceLoggingState = deviceLogging;
      this.deviceLogging = deviceLogging.messages ?? [];
      this.hasMore = !!deviceLogging.continuationToken;

      this.isLoading = false;
      this.isLoadingMore = false;

      this.cdr.markForCheck();
    });
  }
}